import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  Chip,
  Box,
} from '@mui/material';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import { Permission, Role } from '..';

type Props = {
  open: boolean;
  onClose: () => void;
  role: Role | undefined;
  onEditPermissions: () => void;
};

const RolePermissionsSummary: React.FC<Props> = ({
  open,
  onClose,
  role,
  onEditPermissions,
}) => {
  const permissionsByType = useMemo(() => {
    if (!role) return {};

    const grouped: Record<string, Permission[]> = {};
    role.permissions.forEach((perm) => {
      if (!grouped[perm.type]) grouped[perm.type] = [];
      grouped[perm.type].push(perm);
    });

    Object.keys(grouped).forEach((type) => {
      grouped[type].sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
      );
    });
    return grouped;
  }, [role]);

  const permissionsSortedByType = Object.keys(permissionsByType).sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' }),
  );

  const totalPermissions = role?.permissions.length || 0;
  const totalTypes = permissionsSortedByType.length;

  const buttons: CustomButtonProps[] = [
    {
      name: 'Editar Permisos',
      action: onEditPermissions,
      color: 'primary',
    },
  ];

  if (!role) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          m: 2,
          height: '80vh',
          maxHeight: '800px',
          minHeight: '600px',
        },
      }}
    >
      <DialogContent sx={{ pb: 4, overflow: 'hidden' }}>
        <HeaderWithActions
          title={`Resumen de Permisos: ${role.name}`}
          buttons={buttons}
        />

        <Box
          sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-evenly',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" color="primary">
                {totalPermissions}
              </Typography>
              <Typography>Permisos Totales</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" color="primary">
                {totalTypes}
              </Typography>
              <Typography>Tipos de Permisos</Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          Permisos por Tipo
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{ height: 'calc(100% - 200px)', overflow: 'auto' }}
        >
          {permissionsSortedByType.map((type) => (
            <Grid item xs={12} md={6} key={type}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      {type}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {permissionsByType[type].length}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {permissionsByType[type].map((permission) => (
                      <Chip
                        key={permission.name}
                        label={permission.label}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {permissionsSortedByType.length === 0 && (
          <Container sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Este rol no tiene permisos asignados
            </Typography>
          </Container>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RolePermissionsSummary;
